import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCms } from '../contexts/CmsContext';
import Header from './Header';
import Footer from './Footer';
import ScrollAnimation from './ScrollAnimation';
import './Page.css';
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaPaperPlane,
  FaShareAlt,
  FaLink,
  FaUsers
} from 'react-icons/fa';

const Contact = () => {
  const { language } = useLanguage();
  const { getContent } = useCms();
  const t = getContent(language);
  const c = t.contactPage;
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    industry: 'oilGas',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Split full name into first and last name
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');

      // Append company name to message
      const finalMessage = `Company: ${formData.companyName}\n\n${formData.message}`;

      const payload = {
        firstName: firstName,
        lastName: lastName,
        email: formData.email,
        phone: '', // Not collected in form
        projectType: formData.industry,
        message: finalMessage
      };

      const apiUrl = '/backend/contact.php';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        alert(c.toast.success);
        setFormData({
          fullName: '',
          email: '',
          companyName: '',
          industry: 'oilGas',
          message: ''
        });
      } else {
        alert(result.error || 'Error sending message');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Connection error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div dir={language === 'ar' ? 'rtl' : 'ltr'} className={`page ${language === 'ar' ? 'rtl' : 'ltr'} animate-fade-in`}>
      <Header />

      <main
        className={`${
          language === 'ar' ? 'font-cairo' : 'font-sans'
        } bg-white text-slate-900 transition-colors duration-300`}
      >
        <ScrollAnimation
          animation="fade-up"
          className="relative h-[60vh] min-h-[500px] flex items-center overflow-hidden pt-32"
        >
          <img
            alt="AMHBM Headquarters"
            className="absolute inset-0 w-full h-full object-cover"
            src={c.hero.image}
          />
          <div className="absolute inset-0 hero-light-overlay" />
          <div className="relative z-10 px-6 lg:px-12 max-w-[1440px] mx-auto w-full">
            <div className="max-w-2xl">
              <span className="inline-block text-primary text-xs font-bold tracking-[0.3em] uppercase mb-4">
                {c.hero.badge}
              </span>
              <h1 className="text-slate-900 text-4xl md:text-6xl font-black mb-6 leading-tight">
                {c.hero.titleLine1} <br />
                <span className="text-primary">{c.hero.titleEmphasis}</span>
              </h1>
              <p className="text-slate-600 text-lg md:text-xl font-light leading-relaxed mb-10">
                {c.hero.subtitle}
              </p>
            </div>
          </div>
        </ScrollAnimation>

        <ScrollAnimation
          animation="fade-up"
          delay={100}
          className="py-20 bg-background-light relative z-20 -mt-16"
        >
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 contact-card transition-all duration-300">
                <div className="w-16 h-16 bg-secondary/10 rounded-2xl flex items-center justify-center mb-8">
                  <FaMapMarkerAlt className="text-secondary text-4xl" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{c.cards.visitTitle}</h3>
                <p className="text-slate-500 leading-relaxed">
                  {c.cards.visitLine1}
                  <br />
                  {c.cards.visitLine2}
                  <br />
                  {c.cards.visitLine3}
                </p>
              </div>
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 contact-card transition-all duration-300">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-8">
                  <FaPhoneAlt className="text-primary text-4xl" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{c.cards.callTitle}</h3>
                <p className="text-slate-500 leading-relaxed mb-2">
                  <span className="font-bold text-slate-700">{c.cards.callMainLabel}</span> <span dir="ltr">{c.cards.callMainNumber}</span>
                </p>
                <p className="text-slate-500 leading-relaxed">
                  <span className="font-bold text-slate-700">{c.cards.callSalesLabel}</span> <span dir="ltr">{c.cards.callSalesNumber}</span>
                </p>
              </div>
              <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50 contact-card transition-all duration-300">
                <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mb-8">
                  <FaEnvelope className="text-accent text-4xl" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{c.cards.emailTitle}</h3>
                <p className="text-slate-500 leading-relaxed mb-2">
                  <span className="font-bold text-slate-700">{c.cards.emailGeneralLabel}</span> info@amhbm.com
                </p>
                <p className="text-slate-500 leading-relaxed">
                  <span className="font-bold text-slate-700">{c.cards.emailSalesLabel}</span> sales@amhbm.com
                </p>
              </div>
            </div>
          </div>
        </ScrollAnimation>

        <ScrollAnimation
          animation="fade-up"
          delay={200}
          className="py-24 bg-white"
        >
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-16">
              <div className="lg:col-span-8">
                <h2 className="text-4xl font-black mb-8 text-slate-900">{c.form.title}</h2>
                <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {c.form.fullName}
                    </label>
                    <input
                      className="w-full px-5 py-4 bg-slate-50 border-transparent focus:border-primary focus:bg-white focus:ring-0 rounded-xl transition-all"
                      type="text"
                      name="fullName"
                      placeholder={c.form.fullNamePlaceholder}
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {c.form.companyName}
                    </label>
                    <input
                      className="w-full px-5 py-4 bg-slate-50 border-transparent focus:border-primary focus:bg-white focus:ring-0 rounded-xl transition-all"
                      type="text"
                      name="companyName"
                      placeholder={c.form.companyNamePlaceholder}
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {c.cards.emailGeneralLabel}
                    </label>
                    <input
                      className="w-full px-5 py-4 bg-slate-50 border-transparent focus:border-primary focus:bg-white focus:ring-0 rounded-xl transition-all"
                      type="email"
                      name="email"
                      placeholder=""
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {c.form.industry}
                    </label>
                    <select
                      className="w-full px-5 py-4 bg-slate-50 border-transparent focus:border-primary focus:bg-white focus:ring-0 rounded-xl transition-all"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                    >
                      <option value="oilGas">{c.form.industryOptions.oilGas}</option>
                      <option value="infrastructure">{c.form.industryOptions.infrastructure}</option>
                      <option value="mining">{c.form.industryOptions.mining}</option>
                      <option value="manufacturing">{c.form.industryOptions.manufacturing}</option>
                      <option value="other">{c.form.industryOptions.other}</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {c.form.message}
                    </label>
                    <textarea
                      className="w-full px-5 py-4 bg-slate-50 border-transparent focus:border-primary focus:bg-white focus:ring-0 rounded-xl transition-all"
                      rows="6"
                      name="message"
                      placeholder={c.form.messagePlaceholder}
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`bg-primary text-white px-12 py-5 rounded-xl font-bold uppercase tracking-widest hover:bg-teal-deep transition-all shadow-xl inline-flex items-center gap-3 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isSubmitting ? 'Sending...' : c.form.submit}
                      {!isSubmitting && <FaPaperPlane />}
                    </button>
                  </div>
                </form>
              </div>
              <div className="lg:col-span-4 space-y-12">
                <div className="bg-teal-deep text-white p-10 rounded-3xl">
                  <h4 className="text-xl font-bold mb-6 border-b border-white/10 pb-4">
                    {c.sidebar.businessHoursTitle}
                  </h4>
                  <ul className="space-y-4 text-emerald-50/80 text-sm">
                    <li className="flex justify-between">
                      <span>{c.sidebar.sundayThursday}</span>
                      <span className="font-bold text-white">08:00 - 18:00</span>
                    </li>
                    <li className="flex justify-between">
                      <span>{c.sidebar.friday}</span>
                      <span className="font-bold text-white">{c.sidebar.closed}</span>
                    </li>
                    <li className="flex justify-between">
                      <span>{c.sidebar.saturday}</span>
                      <span className="font-bold text-white">09:00 - 13:00</span>
                    </li>
                  </ul>
                  <h4 className="text-xl font-bold mb-6 mt-12 border-b border-white/10 pb-4">
                    {c.sidebar.connectTitle}
                  </h4>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-secondary hover:text-slate-950 transition-all"
                    >
                      <FaShareAlt />
                    </button>
                    <button
                      type="button"
                      className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-secondary hover:text-slate-950 transition-all"
                    >
                      <FaLink />
                    </button>
                    <button
                      type="button"
                      className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-secondary hover:text-slate-950 transition-all"
                    >
                      <FaUsers />
                    </button>
                  </div>
                </div>
                <div className="bg-slate-50 p-10 rounded-3xl border border-slate-100">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-8 text-center">
                    {c.sidebar.certifiedTitle}
                  </h4>
                  <div className="grid grid-cols-2 gap-8 items-center justify-items-center opacity-60">
                    <div className="text-2xl font-black text-slate-900">ASME</div>
                    <div className="text-2xl font-black text-slate-900">ASTM</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollAnimation>


      </main>

      <Footer />
    </div>
  );
};

export default Contact;
